import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import useMediaQuery from '@mui/material/useMediaQuery';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import OilIcon from '@mui/icons-material/WaterDrop';
import SteelIcon from '@mui/icons-material/Construction';
import { useFetchCommodityPrice } from '../../hooks/useFetchCommodityPrice';
import TradingViewWidget from './TradingViewWidget';

export default function HeaderStocks() {
  const [openModal, setOpenModal] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const { data, loading, error } = useFetchCommodityPrice();
  const isMobile = useMediaQuery('(max-width:750px)');

  const handleOpen = (modalName) => {
    setOpenModal(modalName);
  };

  const handleClose = () => {
    setOpenModal(null);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getSymbol = (modalName) => {
    const symbolMap = {
      wti: 'BLACKBULL:WTI',
      brent: 'BLACKBULL:BRENT',
      hrc: 'COMEX:HRC1!',
    };

    return symbolMap[modalName];
  };

  if (loading) {
    return (
      <Box sx={{ padding: '16px', textAlign: 'center' }}>
        <Typography variant="h8">Loading commodity prices...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: '16px', textAlign: 'center' }}>
        {/* <Typography variant="h6" color="error">
          Error: {error.message}
        </Typography> */}
      </Box>
    );
  }

  const getDetailsLink = (modalName) => {
    const detailsLinkMap = {
      wti: 'https://tradingeconomics.com/commodity/crude-oil',
      brent: 'https://tradingeconomics.com/commodity/brent-crude-oil',
      hrc: 'https://tradingeconomics.com/commodity/hrc-steel',
    };

    return detailsLinkMap[modalName] || 'https://tradingeconomics.com/commodities'; // Default link
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        padding: '8px 16px',
        borderRadius: '8px',
      }}
    >
      {isMobile ? (
        <>
          <IconButton onClick={handleMenuOpen}>
            <QueryStatsIcon />
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <List>
              <ListItem
                button
                onClick={() => {
                  handleMenuClose();
                  handleOpen('wti');
                }}
              >
                <ListItemIcon>
                  <OilIcon sx={{ color: '#1976d2' }} />
                </ListItemIcon>
                <ListItemText primary={`WTI Oil: ${data?.wtiPrice || 'N/A'}`} />
              </ListItem>
              <ListItem
                button
                onClick={() => {
                  handleMenuClose();
                  handleOpen('brent');
                }}
              >
                <ListItemIcon>
                  <OilIcon sx={{ color: '#1976d2' }} />
                </ListItemIcon>
                <ListItemText primary={`Brent Oil: ${data?.brntPrice || 'N/A'}`} />
              </ListItem>
              <ListItem
                button
                onClick={() => {
                  handleMenuClose();
                  handleOpen('hrc');
                }}
              >
                <ListItemIcon>
                  <SteelIcon sx={{ color: '#0a2455' }} />
                </ListItemIcon>
                <ListItemText primary={`HRC Steel: ${data?.hrcPrice || 'N/A'}`} />
              </ListItem>
            </List>
          </Menu>
        </>
      ) : (
        <Box sx={{ display: 'flex', gap: '16px' }}>
          <Box
            sx={{
              padding: '5px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '160px',
            }}
            onClick={() => handleOpen('wti')}
          >
            <OilIcon sx={{ color: '#1976d2' }} />
            <Typography sx={{ fontWeight: 'bold', fontSize: '14px', flexGrow: 1, marginLeft: '8px' }}>
              WTI Oil
            </Typography>
            <Typography sx={{ fontWeight: 'bold' }}>{data?.wtiPrice || 'N/A'}</Typography>
          </Box>

          <Box
            sx={{
              padding: '5px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '160px',
            }}
            onClick={() => handleOpen('brent')}
          >
            <OilIcon sx={{ color: '#1976d2' }} />
            <Typography sx={{ fontWeight: 'bold', fontSize: '14px', flexGrow: 1, marginLeft: '8px' }}>
              Brent Oil
            </Typography>
            <Typography sx={{ fontWeight: 'bold' }}>{data?.brntPrice || 'N/A'}</Typography>
          </Box>

          <Box
            sx={{
              padding: '5px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '185px',
            }}
            onClick={() => handleOpen('hrc')}
          >
            <SteelIcon sx={{ color: '#0a2455' }} />
            <Typography sx={{ fontWeight: 'bold', fontSize: '14px', flexGrow: 1, marginLeft: '8px' }}>
              HRC Steel
            </Typography>
            <Typography sx={{ fontWeight: 'bold' }}>{data?.hrcPrice || 'N/A'}</Typography>
          </Box>
        </Box>
      )}

      {/* Modal for TradingView Widget */}
      <Modal open={Boolean(openModal)} onClose={handleClose}>
        <Box sx={{ ...modalStyles, width: '80%', maxWidth: '1200px' }}>
          <Typography variant="h6" gutterBottom>
            {openModal === 'wti' && 'WTI Oil Chart'}
            {openModal === 'brent' && 'Brent Oil Chart'}
            {openModal === 'hrc' && 'HRC Steel Chart'}
          </Typography>
          <TradingViewWidget symbol={getSymbol(openModal)} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, marginTop: 2 }}>
            {/* More Details Button */}
            <Box
              onClick={() => window.open(getDetailsLink(openModal), '_blank')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                },
              }}
            >
              <Typography sx={{ fontWeight: 'bold', fontSize: '14px', color: '#1976d2' }}>More Details</Typography>
            </Box>

            {/* Close Button */}
            <Box
              onClick={handleClose}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                },
              }}
            >
              <Typography sx={{ fontWeight: 'bold', fontSize: '14px', color: '#1976d2' }}>Close</Typography>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

const modalStyles = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '1300px',
  height: '890px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 2,
  borderRadius: '8px',
};
